import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ListChecks, Building2, Calendar, ArrowRight, ExternalLink, Loader2, Sparkles, Mic,
    ShieldCheck, X, ChevronDown, ChevronUp, History, MessageCircle,
} from "lucide-react";
import { ApplicantChatPanel } from "@/components/messaging/ApplicantChatPanel";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const COLUMNS = [
    { key: "applied", label: "Applied", accent: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30" },
    { key: "reviewing", label: "Reviewing", accent: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30" },
    { key: "interview", label: "Interview", accent: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
    { key: "offer", label: "Offer", accent: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
    { key: "rejected", label: "Rejected", accent: "bg-muted text-muted-foreground border-border" },
    { key: "archived", label: "Archived", accent: "bg-muted text-muted-foreground border-border" },
];

const timeAgo = (iso) => {
    if (!iso) return "";
    const d = Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
    if (d < 1) return "today";
    if (d < 7) return `${d}d ago`;
    if (d < 30) return `${Math.round(d / 7)}w ago`;
    return `${Math.round(d / 30)}mo ago`;
};

const STAGE_TONE = {
    applied: "text-blue-700 dark:text-blue-400",
    new: "text-blue-700 dark:text-blue-400",
    reviewing: "text-violet-700 dark:text-violet-400",
    shortlisted: "text-emerald-700 dark:text-emerald-400",
    interview: "text-amber-700 dark:text-amber-400",
    offer: "text-emerald-700 dark:text-emerald-400",
    hired: "text-emerald-700 dark:text-emerald-400",
    rejected: "text-muted-foreground",
    archived: "text-muted-foreground",
};

const STAGE_FRIENDLY = {
    new: "Application received",
    applied: "Application received",
    reviewing: "The hiring team is reviewing your application",
    shortlisted: "You've been shortlisted",
    interview: "Moved to interview",
    offer: "Offer extended",
    hired: "Hired",
    rejected: "The team went with other candidates",
    archived: "Archived",
};

const STAGE_ORDER = ["applied", "reviewing", "shortlisted", "hired"];
const STAGE_STEPPER_LABEL = { applied: "Applied", reviewing: "Reviewing", shortlisted: "Shortlisted", hired: "Hired" };

const StatusStepper = ({ status, testIdPrefix = "" }) => {
    // Map raw status → position in the linear pipeline. "new" also counts
    // as position 0 (Applied) since that's the applicant-visible label.
    const s = (status || "applied").toLowerCase();
    const isRejected = s === "rejected" || s === "archived";
    const currentIdx = STAGE_ORDER.indexOf(s === "new" ? "applied" : s);
    return (
        <div className="flex items-center gap-1.5 mt-2" data-testid={`${testIdPrefix}stepper-${status || "applied"}`}>
            {STAGE_ORDER.map((stage, idx) => {
                const active = !isRejected && currentIdx >= idx;
                const isCurrent = !isRejected && currentIdx === idx;
                return (
                    <React.Fragment key={stage}>
                        <div
                            className={`h-1.5 w-1.5 rounded-full transition-colors ${
                                isRejected
                                    ? "bg-muted-foreground/30"
                                    : active
                                    ? "bg-emerald-500"
                                    : "bg-border"
                            } ${isCurrent ? "ring-2 ring-emerald-500/30 h-2 w-2" : ""}`}
                            title={STAGE_STEPPER_LABEL[stage]}
                        />
                        {idx < STAGE_ORDER.length - 1 && (
                            <div
                                className={`h-[1px] flex-1 ${
                                    isRejected
                                        ? "bg-muted-foreground/20"
                                        : currentIdx > idx
                                        ? "bg-emerald-500"
                                        : "bg-border"
                                }`}
                            />
                        )}
                    </React.Fragment>
                );
            })}
            {isRejected && (
                <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                    {s}
                </span>
            )}
        </div>
    );
};

const AppCard = ({ app, onStatus, onRehearse, expandedId, setExpandedId, chatOpenId, setChatOpenId }) => {
    const isExpanded = expandedId === app.id;
    const isChatOpen = chatOpenId === app.id;
    const timeline = app.timeline || [];
    return (
    <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        data-testid={`app-card-${app.id}`}
        className="card-soft p-4"
    >
        <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
                <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-sm text-foreground truncate">{app.job_title || "Role"}</h3>
                    {app.has_unread && (
                        <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                            data-testid={`app-unread-${app.id}`}
                            title="New update from the hiring team"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            new
                        </span>
                    )}
                </div>
                <div className="text-xs text-muted-foreground truncate flex items-center gap-1 mt-0.5">
                    <Building2 size={11} /> {app.company || "Company"}
                </div>
            </div>
            {app.job_id && (
                <Link
                    to={`/careers/${app.job_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-emerald-600"
                    aria-label="Open job"
                >
                    <ExternalLink size={13} />
                </Link>
            )}
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <Calendar size={10} /> Applied {timeAgo(app.created_at)}
        </div>
        <StatusStepper status={app.status} testIdPrefix={`app-${app.id}-`} />
        <div className="mt-3">
            <Select value={app.status || "applied"} onValueChange={(v) => onStatus(app, v)}>
                <SelectTrigger data-testid={`app-status-${app.id}`} className="h-8 text-xs">
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
            </Select>
        </div>
        <button
            onClick={() => onRehearse(app)}
            className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[11px] font-semibold rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 transition-colors px-2 py-1.5"
            data-testid={`app-rehearse-${app.id}`}
        >
            <Mic size={11} /> Rehearse this interview
        </button>
        {/* Chat toggle — expands under the card. */}
        <button
            type="button"
            onClick={() => setChatOpenId(isChatOpen ? null : app.id)}
            className="mt-2 w-full inline-flex items-center justify-between gap-1 text-[11px] font-semibold rounded-lg border border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400 hover:bg-sky-500/20 transition-colors px-2 py-1.5"
            data-testid={`app-chat-toggle-${app.id}`}
        >
            <span className="inline-flex items-center gap-1">
                <MessageCircle size={11} /> Message hiring team
                {app.has_unread_messages && (
                    <span className="ml-1 w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" title="Unread message" />
                )}
            </span>
            {isChatOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
        {isChatOpen && (
            <div className="mt-2">
                <ApplicantChatPanel
                    applicationId={app.id}
                    role="applicant"
                    testIdPrefix={`app-chat-${app.id}`}
                />
            </div>
        )}
        {/* Employer-authored timeline — read-only, no admin identity leaked. */}
        {timeline.length > 0 && (
            <div className="mt-3 pt-3 border-t border-border">
                <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : app.id)}
                    className="w-full flex items-center justify-between text-[10px] uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground transition"
                    data-testid={`app-timeline-toggle-${app.id}`}
                >
                    <span className="inline-flex items-center gap-1">
                        <History size={10} /> Employer timeline
                        <span className="text-muted-foreground/70 normal-case tabular-nums ml-1">
                            · {timeline.length} update{timeline.length === 1 ? "" : "s"}
                        </span>
                    </span>
                    {isExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                </button>
                {isExpanded && (
                    <ol
                        className="mt-2.5 relative pl-3.5 space-y-2 border-l border-border"
                        data-testid={`app-timeline-${app.id}`}
                    >
                        {[...timeline]
                            .sort((x, y) => (x.at > y.at ? -1 : 1))
                            .map((evt, idx) => {
                                const key = (evt.to_status || "").toLowerCase();
                                return (
                                    <li key={`${evt.at}-${idx}`} className="relative">
                                        <span className={`absolute -left-[7px] top-1.5 h-1.5 w-1.5 rounded-full ${STAGE_TONE[key] ? "bg-current" : "bg-muted-foreground"} ${STAGE_TONE[key] || ""}`} />
                                        <div className={`text-[11px] font-medium ${STAGE_TONE[key] || "text-foreground"}`}>
                                            {STAGE_FRIENDLY[key] || `Moved to ${key}`}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">
                                            {timeAgo(evt.at)}
                                        </div>
                                    </li>
                                );
                            })}
                    </ol>
                )}
            </div>
        )}
    </motion.div>
    );
};

const ApplicationTracker = () => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("kanban");
    const [idvStatus, setIdvStatus] = useState(null); // {verified, has_pending_session}
    const [idvStarting, setIdvStarting] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(
        typeof window !== "undefined" && window.localStorage.getItem("verify_banner_dismissed") === "1",
    );
    const [expandedTimelineId, setExpandedTimelineId] = useState(null);
    const [chatOpenId, setChatOpenId] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [{ data: appsData }, { data: idv }] = await Promise.all([
                api.get("/professional/applications"),
                api.get("/talent/id-verification/status").catch(() => ({ data: null })),
            ]);
            setApps(appsData);
            setIdvStatus(idv);
            // Landing on the tracker == the applicant has seen the timeline.
            // Fire-and-forget clears the sidebar red-dot for next render.
            api.post("/professional/applications/timeline-seen").catch(() => { /* fail soft */ });
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const startIdVerification = async () => {
        try {
            setIdvStarting(true);
            const { data } = await api.post("/talent/id-verification/start");
            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error("Could not open verification.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setIdvStarting(false);
        }
    };

    const dismissBanner = () => {
        window.localStorage.setItem("verify_banner_dismissed", "1");
        setBannerDismissed(true);
    };

    // Show the banner ONLY when the user has actually applied to something
    // (highest-intent moment) AND an employer has requested an identity
    // check for them AND they aren't verified yet AND haven't dismissed.
    // iter 148 — no self-serve identity verification, so the banner is
    // request-gated. If no employer has requested one, we hide the banner.
    const showVerifyBanner = !bannerDismissed
        && apps.length > 0
        && idvStatus
        && !idvStatus.verified
        && !!idvStatus.requested;

    const grouped = useMemo(() => {
        const g = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
        for (const a of apps) {
            const k = COLUMNS.some((c) => c.key === a.status) ? a.status : "applied";
            g[k].push(a);
        }
        return g;
    }, [apps]);

    const changeStatus = async (app, status) => {
        try {
            await api.patch(`/professional/applications/${app.id}`, { status });
            setApps((xs) => xs.map((x) => x.id === app.id ? { ...x, status } : x));
            toast.success(`Moved to ${status}.`);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const rehearse = (app) => {
        const params = new URLSearchParams();
        if (app.job_title) params.set("role", app.job_title);
        if (app.company) params.set("company", app.company);
        if (app.job_id) params.set("job_id", app.job_id);
        if (app.id) params.set("application_id", app.id);
        params.set("source", "application");
        navigate(`/professional/interview-coach?${params.toString()}`);
    };

    if (loading) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid="application-tracker-page">
            {showVerifyBanner && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
                    data-testid="tracker-verify-banner"
                    className="mb-6 rounded-2xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-4 flex items-start gap-3 flex-wrap"
                >
                    <div className="h-9 w-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
                        <ShieldCheck size={17} />
                    </div>
                    <div className="flex-1 min-w-[240px]">
                        <p className="font-heading font-semibold text-sm text-foreground">
                            {idvStatus?.has_pending_session
                                ? "Your ID check is in review with Stripe."
                                : "Employers just saw your application."}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 max-w-xl">
                            {idvStatus?.has_pending_session
                                ? "We'll flip your Verified badge on automatically the moment approval lands — usually a few minutes."
                                : "An employer has requested a government-ID + selfie check for your profile. Takes about two minutes — Simonara covers the cost."}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {!idvStatus?.has_pending_session && (
                            <button
                                onClick={startIdVerification}
                                disabled={idvStarting}
                                data-testid="tracker-verify-cta"
                                className="btn-green text-xs inline-flex items-center disabled:opacity-60"
                            >
                                {idvStarting
                                    ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Opening…</>
                                    : <>Start verification <ArrowRight size={12} className="ml-1.5" /></>}
                            </button>
                        )}
                        <button
                            onClick={dismissBanner}
                            data-testid="tracker-verify-dismiss"
                            aria-label="Dismiss verification banner"
                            className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted/50"
                        >
                            <X size={14} />
                        </button>
                    </div>
                </motion.div>
            )}

            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div>
                    <p className="overline">Your pipeline</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Application Tracker</h1>
                    <p className="text-sm text-muted-foreground mt-1">{apps.length} application{apps.length === 1 ? "" : "s"} total.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setView("kanban")} data-testid="tracker-view-kanban" className={`px-3 py-2 rounded-full text-xs font-semibold border ${view === "kanban" ? "bg-brand-navy text-white border-brand-navy" : "bg-card border-border"}`}>Kanban</button>
                    <button onClick={() => setView("list")} data-testid="tracker-view-list" className={`px-3 py-2 rounded-full text-xs font-semibold border ${view === "list" ? "bg-brand-navy text-white border-brand-navy" : "bg-card border-border"}`}>List</button>
                </div>
            </div>

            {apps.length === 0 ? (
                <div className="card-soft p-12 text-center">
                    <ListChecks size={22} className="mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">No applications yet.</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Every job you apply to through Simonara will land here automatically — organized by stage so you never lose track.</p>
                    <Link to="/professional/matches" className="btn-green mt-6 inline-flex text-sm">Browse Job Matches <ArrowRight size={13} className="ml-2" /></Link>
                </div>
            ) : view === "kanban" ? (
                <div className="overflow-x-auto pb-4">
                    <div className="grid grid-flow-col auto-cols-[280px] gap-4 min-w-max">
                        {COLUMNS.map((c) => (
                            <div key={c.key} data-testid={`column-${c.key}`} className="flex flex-col">
                                <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider self-start ${c.accent}`}>
                                    {c.label} <span className="rounded-full bg-white/60 dark:bg-black/40 px-1.5">{grouped[c.key].length}</span>
                                </div>
                                <div className="mt-3 space-y-3 min-h-[100px]">
                                    {grouped[c.key].map((a) => <AppCard key={a.id} app={a} onStatus={changeStatus} onRehearse={rehearse} expandedId={expandedTimelineId} setExpandedId={setExpandedTimelineId} chatOpenId={chatOpenId} setChatOpenId={setChatOpenId} />)}
                                    {grouped[c.key].length === 0 && (
                                        <div className="rounded-2xl border-2 border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                                            Nothing here yet.
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {apps.map((a) => {
                        const isExpanded = expandedTimelineId === a.id;
                        const timeline = a.timeline || [];
                        return (
                        <div key={a.id} data-testid={`app-row-${a.id}`} className="card-soft p-4">
                            <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <h3 className="font-heading font-semibold text-foreground truncate">{a.job_title || "Role"}</h3>
                                <div className="text-xs text-muted-foreground truncate">{a.company} · Applied {timeAgo(a.created_at)}</div>
                            </div>
                            <div className="w-40 shrink-0">
                                <Select value={a.status || "applied"} onValueChange={(v) => changeStatus(a, v)}>
                                    <SelectTrigger className="h-9 text-xs">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            {a.job_id && (
                                <Link to={`/careers/${a.job_id}`} target="_blank" rel="noopener noreferrer" className="btn-outline text-xs px-3 py-2 inline-flex items-center gap-1">
                                    View job <ExternalLink size={11} />
                                </Link>
                            )}
                            <button
                                onClick={() => rehearse(a)}
                                data-testid={`app-rehearse-row-${a.id}`}
                                className="inline-flex items-center gap-1 text-xs font-semibold rounded-lg border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20 transition-colors px-3 py-2"
                            >
                                <Mic size={11} /> Rehearse
                            </button>
                            </div>
                            {timeline.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-border">
                                    <button
                                        type="button"
                                        onClick={() => setExpandedTimelineId(isExpanded ? null : a.id)}
                                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground transition"
                                        data-testid={`app-timeline-toggle-${a.id}`}
                                    >
                                        <History size={10} /> Employer timeline
                                        <span className="text-muted-foreground/70 normal-case tabular-nums ml-1">
                                            · {timeline.length} update{timeline.length === 1 ? "" : "s"}
                                        </span>
                                        {isExpanded ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                                    </button>
                                    {isExpanded && (
                                        <ol className="mt-2.5 relative pl-3.5 space-y-2 border-l border-border" data-testid={`app-timeline-${a.id}`}>
                                            {[...timeline]
                                                .sort((x, y) => (x.at > y.at ? -1 : 1))
                                                .map((evt, idx) => {
                                                    const key = (evt.to_status || "").toLowerCase();
                                                    return (
                                                        <li key={`${evt.at}-${idx}`} className="relative">
                                                            <span className={`absolute -left-[7px] top-1.5 h-1.5 w-1.5 rounded-full bg-current ${STAGE_TONE[key] || "text-muted-foreground"}`} />
                                                            <div className={`text-[11px] font-medium ${STAGE_TONE[key] || "text-foreground"}`}>
                                                                {STAGE_FRIENDLY[key] || `Moved to ${key}`}
                                                            </div>
                                                            <div className="text-[10px] text-muted-foreground mt-0.5">{timeAgo(evt.at)}</div>
                                                        </li>
                                                    );
                                                })}
                                        </ol>
                                    )}
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-8 card-soft p-4 flex items-start gap-3">
                <Sparkles size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                    New applications land in <b>Applied</b> automatically. Move them along as things progress — hiring managers on our team will see your movement and can help nudge stalled conversations.
                </p>
            </div>
        </div>
    );
};

export default ApplicationTracker;
